import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@roger/ui-components';
import { DateField, DropdownField, Field } from '../FormFields';
import {
  createRemittanceRemittancesPost as createRemittance,
  getRemittanceDetailRemittancesRemittanceIdGet as getRemittanceDetail,
  listRemittancePayorsRemittancesPayorsGet as listRemittancePayors,
  searchRemittanceClaimsRemittancesClaimsSearchGet as searchRemittanceClaims,
  updateRemittanceWorksheetRemittancesRemittanceIdPut as updateRemittanceWorksheet,
} from '../../services/api/sdk.gen';
import type {
  RemittanceAdjustmentDetail,
  RemittanceAdjustmentUpsert,
  RemittanceClaimSearchRow,
  RemittanceDetailResponse,
  RemittanceManualCreate,
  RemittancePaymentDetail,
  RemittancePaymentUpsert,
  RemittanceServiceLineDetail,
  RemittanceServiceLineUpsert,
  RemittanceWorksheetUpdate,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getTodayInPT } from '../../utils/dateUtils';
import { useStore } from '../../store';

export interface RemittanceWorksheetEditorProps {
  mode: 'create' | 'edit';
  remittanceId?: string;
  onClose: () => void;
  onSaved: () => void;
}

type AdjustmentState = {
  id?: string;
  group_code: string;
  reason_code: string;
  adjustment_amount: string;
  fileDerived?: boolean;
  preserveBlankReasonCode: boolean;
};

type ServiceLineState = {
  id?: string;
  service_date: string;
  serviceDateFromRange: boolean;
  line_item_charge_amount: string;
  line_item_provider_payment_amount: string;
  units_of_service_paid_count: string;
  remark_codes: string;
  adjustments: AdjustmentState[];
};

type PaymentState = {
  id?: string;
  claim_id: string;
  claim_number: string;
  linkedClaimPatientName: string;
  linkedClaimPatientControlNumber: string;
  patient_control_number: string;
  patientControlFileDerived: boolean;
  payer_claim_control_number: string;
  claim_status_code: string;
  total_claim_charge_amount: string;
  claim_payment_amount: string;
  patient_responsibility_amount: string;
  facility_type_code: string;
  claim_frequency_code: string;
  remark_codes: string;
  service_lines: ServiceLineState[];
  adjustments: AdjustmentState[];
};

type EditorState = {
  payor_id: string;
  deposit_date: string;
  check_date: string;
  trace_number: string;
  total: string;
  payment_method_code: string;
  comments: string;
  payments: PaymentState[];
};

const EMPTY_STATE: EditorState = {
  payor_id: '',
  deposit_date: '',
  check_date: '',
  trace_number: '',
  total: '',
  payment_method_code: 'CHK',
  comments: '',
  payments: [],
};

const CLAIM_STATUS_OPTIONS = [
  { value: '1', label: '1 — Processed as Primary' },
  { value: '2', label: '2 — Processed as Secondary' },
  { value: '3', label: '3 — Processed as Tertiary' },
  { value: '4', label: '4 — Denied' },
  { value: '19', label: '19 — Paid variant' },
  { value: '20', label: '20 — Paid variant' },
  { value: '21', label: '21 — Paid variant' },
  { value: '22', label: '22 — Reversal' },
  { value: '23', label: '23 — Forwarded' },
  { value: '25', label: '25 — Predetermination' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'ACH', label: 'ACH' },
  { value: 'CHK', label: 'Check' },
  { value: 'NON', label: 'Non-payment' },
];

const GROUP_CODE_OPTIONS = ['CO', 'PR', 'OA', 'PI', 'CR'].map(value => ({
  value,
  label: value,
}));

const SEARCH_STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'all', label: 'All statuses' },
];

const CLAIM_SEARCH_PAGE_SIZE = 25;

const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;

const toAdjustmentState = (
  adjustment: RemittanceAdjustmentDetail,
  fileDerived: boolean
): AdjustmentState => ({
  id: adjustment.id,
  group_code: adjustment.group_code ?? '',
  reason_code: adjustment.reason_code,
  adjustment_amount: adjustment.adjustment_amount,
  fileDerived,
  preserveBlankReasonCode:
    fileDerived && adjustment.reason_code.trim().length === 0,
});

const toLineState = (
  line: RemittanceServiceLineDetail,
  fileBacked: boolean
): ServiceLineState => ({
  id: line.id,
  service_date: line.service_date ?? line.service_start_date ?? '',
  serviceDateFromRange: !line.service_date && Boolean(line.service_start_date),
  line_item_charge_amount: line.charge_amount ?? '',
  line_item_provider_payment_amount: line.payment_amount ?? '',
  units_of_service_paid_count: line.units ?? '',
  remark_codes: (line.remark_codes ?? []).join(', '),
  adjustments: (line.adjustments ?? []).map(adjustment =>
    toAdjustmentState(adjustment, fileBacked)
  ),
});

const toPaymentState = (
  payment: RemittancePaymentDetail,
  fileBacked: boolean
): PaymentState => ({
  id: payment.id,
  claim_id: payment.claim_id ?? '',
  claim_number: payment.claim_number ?? '',
  linkedClaimPatientName: payment.linked_claim_patient_name ?? '',
  linkedClaimPatientControlNumber:
    payment.linked_claim_patient_control_number ?? '',
  patient_control_number: payment.patient_control_number ?? '',
  patientControlFileDerived:
    fileBacked && Boolean(payment.patient_control_number),
  payer_claim_control_number: payment.payer_claim_control_number ?? '',
  claim_status_code: payment.claim_status_code ?? '',
  total_claim_charge_amount: payment.total_claim_charge_amount ?? '',
  claim_payment_amount: payment.claim_payment_amount ?? '',
  patient_responsibility_amount: payment.patient_responsibility_amount ?? '',
  facility_type_code: payment.facility_type_code ?? '',
  claim_frequency_code: payment.claim_frequency_code ?? '',
  remark_codes: (payment.remark_codes ?? []).join(', '),
  service_lines: (payment.service_lines ?? []).map(line =>
    toLineState(line, fileBacked)
  ),
  adjustments: (payment.claim_adjustments ?? []).map(adjustment =>
    toAdjustmentState(adjustment, fileBacked)
  ),
});

const detailToState = (detail: RemittanceDetailResponse): EditorState => ({
  payor_id: detail.payor_id ?? '',
  deposit_date: detail.deposit_date ?? '',
  check_date: detail.check_date ?? '',
  trace_number: detail.trace_number ?? '',
  total: detail.amount ?? '',
  payment_method_code: detail.payment_method_code ?? '',
  comments: detail.comments ?? '',
  payments: (detail.payments ?? []).map(payment =>
    toPaymentState(payment, detail.source !== 'manual_entry')
  ),
});

const emptyAdjustment = (): AdjustmentState => ({
  group_code: 'CO',
  reason_code: '',
  adjustment_amount: '',
  preserveBlankReasonCode: false,
});

const emptyLine = (): ServiceLineState => ({
  service_date: '',
  serviceDateFromRange: false,
  line_item_charge_amount: '',
  line_item_provider_payment_amount: '',
  units_of_service_paid_count: '',
  remark_codes: '',
  adjustments: [],
});

const splitCodes = (value: string): string[] =>
  value
    .split(',')
    .map(code => code.trim())
    .filter(Boolean);

const isKnownClaimStatus = (value: string): boolean =>
  CLAIM_STATUS_OPTIONS.some(option => option.value === value);

const formatClaimStatus = (status: string): string =>
  status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase())
    .replace(/\bFor\b/g, 'for');

const currency = (value: string): string => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number.isFinite(amount) ? amount : 0);
};

function LockHint({ visible }: { visible: boolean }) {
  return visible ? (
    <span className='text-xs text-muted-foreground' title='From 835 file'>
      From 835 file
    </span>
  ) : null;
}

function RemarkCodesEditor({
  htmlFor,
  value,
  disabled,
  onChange,
}: {
  htmlFor: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const codes = splitCodes(value);
  return (
    <div className='space-y-2'>
      <Field
        label='Remark codes'
        htmlFor={htmlFor}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fieldSize='sm'
      />
      {codes.length > 0 && (
        <div className='flex flex-wrap gap-1' aria-label='Remark code list'>
          {codes.map(code => (
            <Badge key={code} variant='secondary'>
              {code}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function AdjustmentEditor({
  adjustment,
  idPrefix,
  disabled,
  reasonError,
  error,
  onChange,
  onRemove,
}: {
  adjustment: AdjustmentState;
  idPrefix: string;
  disabled: boolean;
  reasonError?: string;
  error?: string;
  onChange: (next: AdjustmentState) => void;
  onRemove: () => void;
}) {
  const groupLocked =
    adjustment.fileDerived &&
    !GROUP_CODE_OPTIONS.some(option => option.value === adjustment.group_code);
  return (
    <div className='grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50/70 p-2 sm:grid-cols-[9rem_minmax(8rem,1fr)_minmax(8rem,1fr)_auto]'>
      {groupLocked ? (
        <div>
          <div className='mb-1 text-xs text-muted-foreground'>Group code</div>
          <Badge variant='outline'>{adjustment.group_code || 'Unknown'}</Badge>
        </div>
      ) : (
        <DropdownField
          label='Group code'
          htmlFor={`${idPrefix}-group`}
          value={adjustment.group_code}
          options={GROUP_CODE_OPTIONS}
          onChange={group_code => onChange({ ...adjustment, group_code })}
          disabled={disabled}
          fieldSize='sm'
          variant='formSelect'
        />
      )}
      <Field
        label='Reason code'
        htmlFor={`${idPrefix}-reason`}
        value={adjustment.reason_code}
        onChange={reason_code =>
          onChange({
            ...adjustment,
            reason_code,
            preserveBlankReasonCode: false,
          })
        }
        disabled={disabled}
        error={reasonError}
        fieldSize='sm'
      />
      <Field
        label='Amount'
        htmlFor={`${idPrefix}-amount`}
        value={adjustment.adjustment_amount}
        onChange={adjustment_amount =>
          onChange({ ...adjustment, adjustment_amount })
        }
        disabled={disabled}
        error={error}
        fieldSize='sm'
        inputProps={{ inputMode: 'decimal' }}
      />
      {!disabled && (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Remove adjustment'
          onClick={onRemove}
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      )}
    </div>
  );
}

function ServiceLineEditor({
  line,
  disabled,
  errors,
  errorPrefix,
  onChange,
  onRemove,
}: {
  line: ServiceLineState;
  disabled: boolean;
  errors: Record<string, string>;
  errorPrefix: string;
  onChange: (next: ServiceLineState) => void;
  onRemove: () => void;
}) {
  const updateAdjustment = (index: number, next: AdjustmentState) =>
    onChange({
      ...line,
      adjustments: line.adjustments.map((adjustment, adjustmentIndex) =>
        adjustmentIndex === index ? next : adjustment
      ),
    });

  return (
    <div className='space-y-3 rounded border border-slate-200 p-3'>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5'>
        <DateField
          label='Service date'
          htmlFor={`${errorPrefix}-date`}
          value={line.service_date || null}
          onChange={service_date =>
            onChange({
              ...line,
              service_date: service_date ?? '',
              serviceDateFromRange: false,
            })
          }
          disabled={disabled}
          fieldSize='sm'
        />
        <Field
          label='Charge'
          htmlFor={`${errorPrefix}-charge`}
          value={line.line_item_charge_amount}
          onChange={line_item_charge_amount =>
            onChange({ ...line, line_item_charge_amount })
          }
          disabled={disabled}
          error={errors[`${errorPrefix}-charge`]}
          fieldSize='sm'
          inputProps={{ inputMode: 'decimal' }}
        />
        <Field
          label='Paid'
          htmlFor={`${errorPrefix}-paid`}
          value={line.line_item_provider_payment_amount}
          onChange={line_item_provider_payment_amount =>
            onChange({ ...line, line_item_provider_payment_amount })
          }
          disabled={disabled}
          error={errors[`${errorPrefix}-paid`]}
          fieldSize='sm'
          inputProps={{ inputMode: 'decimal' }}
        />
        <Field
          label='Units'
          htmlFor={`${errorPrefix}-units`}
          value={line.units_of_service_paid_count}
          onChange={units_of_service_paid_count =>
            onChange({ ...line, units_of_service_paid_count })
          }
          disabled={disabled}
          fieldSize='sm'
          inputProps={{ inputMode: 'decimal' }}
        />
        <RemarkCodesEditor
          htmlFor={`${errorPrefix}-remarks`}
          value={line.remark_codes}
          onChange={remark_codes => onChange({ ...line, remark_codes })}
          disabled={disabled}
        />
      </div>
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h5 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Line adjustments
          </h5>
          {!disabled && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() =>
                onChange({
                  ...line,
                  adjustments: [...line.adjustments, emptyAdjustment()],
                })
              }
            >
              <Plus className='mr-1 h-3 w-3' /> Add adjustment
            </Button>
          )}
        </div>
        {line.adjustments.map((adjustment, index) => (
          <AdjustmentEditor
            key={adjustment.id ?? `${adjustment.reason_code}-${index}`}
            adjustment={adjustment}
            idPrefix={`${errorPrefix}-adjustment-${index}`}
            disabled={disabled}
            reasonError={errors[`${errorPrefix}-adjustment-${index}-reason`]}
            error={errors[`${errorPrefix}-adjustment-${index}`]}
            onChange={next => updateAdjustment(index, next)}
            onRemove={() =>
              onChange({
                ...line,
                adjustments: line.adjustments.filter(
                  (_, adjustmentIndex) => adjustmentIndex !== index
                ),
              })
            }
          />
        ))}
      </div>
      {!disabled && (
        <Button type='button' variant='ghost' size='sm' onClick={onRemove}>
          <Trash2 className='mr-1 h-3 w-3' /> Remove line
        </Button>
      )}
    </div>
  );
}

function ClaimPaymentEditor({
  payment,
  source,
  disabled,
  errors,
  paymentIndex,
  onChange,
  onRemove,
  onAttachClaim,
  onDetachClaim,
}: {
  payment: PaymentState;
  source: RemittanceDetailResponse['source'] | 'manual_entry';
  disabled: boolean;
  errors: Record<string, string>;
  paymentIndex: number;
  onChange: (next: PaymentState) => void;
  onRemove: () => void;
  onAttachClaim: () => void;
  onDetachClaim: () => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const manualEntry = source === 'manual_entry';
  const statusKnown = isKnownClaimStatus(payment.claim_status_code);
  const paymentKey = payment.id ?? payment.claim_id ?? 'new';
  const idPrefix = `payment-${paymentIndex}`;
  const displayedPatientControlNumber = payment.patientControlFileDerived
    ? payment.patient_control_number
    : payment.linkedClaimPatientControlNumber;
  const claimTitle = payment.claim_id
    ? payment.linkedClaimPatientName ||
      (payment.claim_number
        ? `Claim ${payment.claim_number}`
        : 'Attached claim')
    : 'Unassigned claim';
  const claimSubtitle =
    payment.patientControlFileDerived && payment.patient_control_number
      ? `835 CLP01 ${payment.patient_control_number}`
      : payment.linkedClaimPatientControlNumber
        ? `PCN ${payment.linkedClaimPatientControlNumber}`
        : payment.claim_number && payment.linkedClaimPatientName
          ? `Claim ${payment.claim_number}`
          : payment.claim_id
            ? 'Attached claim'
            : 'Attach a claim or enter the payer control number';
  const updateLine = (index: number, next: ServiceLineState) =>
    onChange({
      ...payment,
      service_lines: payment.service_lines.map((line, lineIndex) =>
        lineIndex === index ? next : line
      ),
    });
  const updateAdjustment = (index: number, next: AdjustmentState) =>
    onChange({
      ...payment,
      adjustments: payment.adjustments.map((adjustment, adjustmentIndex) =>
        adjustmentIndex === index ? next : adjustment
      ),
    });

  const errorPrefix = idPrefix;

  return (
    <div className='rounded-lg border border-slate-200 bg-white'>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type='button'
            className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left'
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} claim ${displayedPatientControlNumber || payment.claim_number || paymentKey}`}
          >
            <span className='flex min-w-0 items-center gap-2'>
              {isOpen ? (
                <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground' />
              ) : (
                <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
              )}
              <span className='min-w-0'>
                <span className='block truncate text-sm font-semibold'>
                  {claimTitle}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {claimSubtitle}
                </span>
              </span>
            </span>
            <span className='text-sm font-semibold'>
              {currency(payment.claim_payment_amount)}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className='space-y-4 border-t px-4 py-4'>
          <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4'>
            <Field
              label='Patient control #'
              htmlFor={`${idPrefix}-pcn`}
              value={displayedPatientControlNumber}
              onChange={patient_control_number =>
                onChange({ ...payment, patient_control_number })
              }
              disabled
              fieldSize='sm'
            />
            <Field
              label='Payer claim control #'
              htmlFor={`${idPrefix}-payer-control`}
              value={payment.payer_claim_control_number}
              onChange={payer_claim_control_number =>
                onChange({ ...payment, payer_claim_control_number })
              }
              disabled
              fieldSize='sm'
            />
            {statusKnown || !payment.claim_status_code ? (
              <DropdownField
                label='CLP02 status'
                htmlFor={`${idPrefix}-status`}
                value={payment.claim_status_code}
                options={CLAIM_STATUS_OPTIONS}
                onChange={claim_status_code =>
                  onChange({ ...payment, claim_status_code })
                }
                disabled={disabled}
                error={errors[`${errorPrefix}-status`]}
                fieldSize='sm'
                variant='formSelect'
              />
            ) : (
              <div>
                <div className='mb-1 text-xs font-medium text-muted-foreground'>
                  CLP02 status
                </div>
                <Badge variant='outline'>
                  {payment.claim_status_code} — From 835 file
                </Badge>
              </div>
            )}
            <Field
              label='Claim charge'
              htmlFor={`${idPrefix}-charge`}
              value={payment.total_claim_charge_amount}
              onChange={total_claim_charge_amount =>
                onChange({ ...payment, total_claim_charge_amount })
              }
              disabled={disabled}
              error={errors[`${errorPrefix}-charge`]}
              fieldSize='sm'
              inputProps={{ inputMode: 'decimal' }}
            />
            <Field
              label='Payment amount'
              htmlFor={`${idPrefix}-paid`}
              value={payment.claim_payment_amount}
              onChange={claim_payment_amount =>
                onChange({ ...payment, claim_payment_amount })
              }
              disabled={disabled}
              error={errors[`${errorPrefix}-paid`]}
              fieldSize='sm'
              inputProps={{ inputMode: 'decimal' }}
            />
            <Field
              label='Patient responsibility'
              htmlFor={`${idPrefix}-responsibility`}
              value={payment.patient_responsibility_amount}
              onChange={patient_responsibility_amount =>
                onChange({ ...payment, patient_responsibility_amount })
              }
              disabled={disabled}
              error={errors[`${errorPrefix}-responsibility`]}
              fieldSize='sm'
              inputProps={{ inputMode: 'decimal' }}
            />
            <Field
              label='Facility type'
              htmlFor={`${idPrefix}-facility`}
              value={payment.facility_type_code}
              onChange={facility_type_code =>
                onChange({ ...payment, facility_type_code })
              }
              disabled={disabled || !manualEntry}
              fieldSize='sm'
              extra={<LockHint visible={!manualEntry} />}
            />
            <Field
              label='Claim frequency'
              htmlFor={`${idPrefix}-frequency`}
              value={payment.claim_frequency_code}
              onChange={claim_frequency_code =>
                onChange({ ...payment, claim_frequency_code })
              }
              disabled={disabled || !manualEntry}
              fieldSize='sm'
              extra={<LockHint visible={!manualEntry} />}
            />
          </div>
          <RemarkCodesEditor
            htmlFor={`${idPrefix}-remarks`}
            value={payment.remark_codes}
            onChange={remark_codes => onChange({ ...payment, remark_codes })}
            disabled={disabled}
          />
          {!disabled && (
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={onAttachClaim}
              >
                <Search className='mr-1 h-3 w-3' />
                {payment.claim_id ? 'Change claim' : 'Attach claim'}
              </Button>
              {payment.claim_id && (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={onDetachClaim}
                >
                  Detach claim
                </Button>
              )}
            </div>
          )}

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Claim adjustments
              </h4>
              {!disabled && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    onChange({
                      ...payment,
                      adjustments: [...payment.adjustments, emptyAdjustment()],
                    })
                  }
                >
                  <Plus className='mr-1 h-3 w-3' /> Add adjustment
                </Button>
              )}
            </div>
            {payment.adjustments.map((adjustment, index) => (
              <AdjustmentEditor
                key={adjustment.id ?? `${adjustment.reason_code}-${index}`}
                adjustment={adjustment}
                idPrefix={`${errorPrefix}-adjustment-${index}`}
                disabled={disabled}
                reasonError={
                  errors[`${errorPrefix}-adjustment-${index}-reason`]
                }
                error={errors[`${errorPrefix}-adjustment-${index}`]}
                onChange={next => updateAdjustment(index, next)}
                onRemove={() =>
                  onChange({
                    ...payment,
                    adjustments: payment.adjustments.filter(
                      (_, adjustmentIndex) => adjustmentIndex !== index
                    ),
                  })
                }
              />
            ))}
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Service lines
              </h4>
              {!disabled && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    onChange({
                      ...payment,
                      service_lines: [...payment.service_lines, emptyLine()],
                    })
                  }
                >
                  <Plus className='mr-1 h-3 w-3' /> Add service line
                </Button>
              )}
            </div>
            {payment.service_lines.map((line, index) => (
              <ServiceLineEditor
                key={line.id ?? index}
                line={line}
                disabled={disabled}
                errors={errors}
                errorPrefix={`${errorPrefix}-line-${index}`}
                onChange={next => updateLine(index, next)}
                onRemove={() =>
                  onChange({
                    ...payment,
                    service_lines: payment.service_lines.filter(
                      (_, lineIndex) => lineIndex !== index
                    ),
                  })
                }
              />
            ))}
          </div>
          {!disabled && (
            <Button type='button' variant='ghost' size='sm' onClick={onRemove}>
              <Trash2 className='mr-1 h-3 w-3' /> Remove claim payment
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function AddClaimsDialog({
  open,
  onOpenChange,
  onAttach,
  unavailableClaimIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAttach: (claim: RemittanceClaimSearchRow) => void;
  unavailableClaimIds: ReadonlySet<string>;
}) {
  const showError = useStore(state => state.showError);
  const [patientName, setPatientName] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [status, setStatus] = useState('submitted');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [serviceFrom, setServiceFrom] = useState('');
  const [serviceTo, setServiceTo] = useState('');
  const [results, setResults] = useState<RemittanceClaimSearchRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);

  const search = async (nextOffset = 0) => {
    setSearching(true);
    try {
      const response = await searchRemittanceClaims({
        query: {
          patient_name: patientName || null,
          claim_number: claimNumber || null,
          status: status as 'submitted' | 'all',
          created_from: createdFrom || null,
          created_to: createdTo || null,
          service_from: serviceFrom || null,
          service_to: serviceTo || null,
          limit: CLAIM_SEARCH_PAGE_SIZE,
          offset: nextOffset,
        },
      });
      if (response.error || !response.data) {
        showError(
          getBusinessErrorMessage(response) ?? 'Failed to search claims'
        );
        return;
      }
      setResults(response.data.items);
      setOffset(nextOffset);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to search remittance claims', error);
      showError('Failed to search claims');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Add claims</DialogTitle>
          <DialogDescription>
            Search this agency’s submitted claims, then attach a claim payment
            row.
          </DialogDescription>
        </DialogHeader>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
          <Field
            label='Patient name'
            htmlFor='remittance-search-patient'
            value={patientName}
            onChange={setPatientName}
            fieldSize='sm'
          />
          <Field
            label='Claim # / PCN'
            htmlFor='remittance-search-claim'
            value={claimNumber}
            onChange={setClaimNumber}
            fieldSize='sm'
          />
          <DropdownField
            label='Status'
            htmlFor='remittance-search-status'
            value={status}
            onChange={setStatus}
            options={SEARCH_STATUS_OPTIONS}
            fieldSize='sm'
            variant='formSelect'
          />
          <DateField
            label='Created from'
            htmlFor='remittance-search-created-from'
            value={createdFrom || null}
            onChange={value => setCreatedFrom(value ?? '')}
            fieldSize='sm'
          />
          <DateField
            label='Created to'
            htmlFor='remittance-search-created-to'
            value={createdTo || null}
            onChange={value => setCreatedTo(value ?? '')}
            fieldSize='sm'
          />
          <DateField
            label='Service from'
            htmlFor='remittance-search-service-from'
            value={serviceFrom || null}
            onChange={value => setServiceFrom(value ?? '')}
            fieldSize='sm'
          />
          <DateField
            label='Service to'
            htmlFor='remittance-search-service-to'
            value={serviceTo || null}
            onChange={value => setServiceTo(value ?? '')}
            fieldSize='sm'
          />
        </div>
        <Button
          type='button'
          size='sm'
          onClick={() => void search(0)}
          disabled={searching}
        >
          <Search className='mr-1 h-3 w-3' />
          {searching ? 'Searching…' : 'Search claims'}
        </Button>
        <div className='max-h-72 overflow-y-auto rounded border'>
          {results.length === 0 ? (
            <p className='p-4 text-sm text-muted-foreground'>
              Search to find claims to attach.
            </p>
          ) : (
            <table className='w-full text-left text-sm'>
              <thead className='bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground'>
                <tr>
                  <th className='p-2'>Patient</th>
                  <th className='p-2'>PCN</th>
                  <th className='p-2'>Claim</th>
                  <th className='p-2'>Status</th>
                  <th className='p-2'>
                    <span className='sr-only'>Attach</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map(claim => {
                  const alreadyAttached = unavailableClaimIds.has(claim.id);
                  const claimLabel =
                    claim.patient_control_number ??
                    claim.claim_number ??
                    'Claim';
                  return (
                    <tr key={claim.id} className='border-t'>
                      <td className='p-2'>{claim.patient_name}</td>
                      <td className='p-2'>
                        {claim.patient_control_number ?? '—'}
                      </td>
                      <td className='p-2'>{claim.claim_number ?? '—'}</td>
                      <td className='p-2'>
                        <Badge variant='outline' className='text-xs'>
                          {formatClaimStatus(claim.status)}
                        </Badge>
                      </td>
                      <td className='p-2 text-right'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          aria-label={
                            alreadyAttached
                              ? `${claimLabel} already attached`
                              : `Attach ${claimLabel}`
                          }
                          disabled={alreadyAttached}
                          onClick={() => {
                            onAttach(claim);
                            onOpenChange(false);
                          }}
                        >
                          {alreadyAttached ? 'Attached' : 'Attach'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {total > 0 && (
          <div className='flex items-center justify-between gap-3 text-sm text-muted-foreground'>
            <span>
              {offset + 1}–{offset + results.length} of {total}
            </span>
            <div className='flex gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  void search(Math.max(0, offset - CLAIM_SEARCH_PAGE_SIZE))
                }
                disabled={searching || offset === 0}
              >
                Previous
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => void search(offset + CLAIM_SEARCH_PAGE_SIZE)}
                disabled={searching || offset + results.length >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RemittanceWorksheetEditor({
  mode,
  remittanceId,
  onClose,
  onSaved,
}: RemittanceWorksheetEditorProps) {
  const showError = useStore(state => state.showError);
  const [state, setState] = useState<EditorState>(EMPTY_STATE);
  const [detail, setDetail] = useState<RemittanceDetailResponse | null>(null);
  const [payors, setPayors] = useState<{ value: string; label: string }[]>([]);
  const [payorsLoading, setPayorsLoading] = useState(true);
  const [payorsError, setPayorsError] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addClaimsOpen, setAddClaimsOpen] = useState(false);
  const [attachPaymentIndex, setAttachPaymentIndex] = useState<number | null>(
    null
  );

  const source = detail?.source ?? 'manual_entry';
  const completed = detail?.status === 'completed';
  const fileBacked = source !== 'manual_entry';
  const identityLocked = completed || fileBacked;
  const unavailableClaimIds = useMemo(
    () =>
      new Set(
        state.payments.flatMap((payment, index) =>
          index !== attachPaymentIndex && payment.claim_id
            ? [payment.claim_id]
            : []
        )
      ),
    [attachPaymentIndex, state.payments]
  );

  const loadPayors = useCallback(async () => {
    setPayorsLoading(true);
    setPayorsError(false);
    try {
      const response = await listRemittancePayors();
      if (response.error || !response.data) {
        setPayorsError(true);
        return;
      }
      setPayors(
        response.data.items.map(payor => ({
          value: payor.id,
          label: payor.name,
        }))
      );
    } catch (error) {
      console.error('Failed to load remittance insurance options', error);
      setPayorsError(true);
    } finally {
      setPayorsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPayors();
  }, [loadPayors]);

  useEffect(() => {
    if (mode !== 'edit' || !remittanceId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    void getRemittanceDetail({
      path: { remittance_id: remittanceId },
    })
      .then(response => {
        if (!active) return;
        if (response.error || !response.data) {
          setLoadError(true);
          return;
        }
        setDetail(response.data);
        setState(detailToState(response.data));
      })
      .catch(error => {
        console.error('Failed to load remittance worksheet', error);
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, remittanceId]);

  const rollups = useMemo(() => {
    const paymentApplied = state.payments.reduce(
      (total, payment) =>
        total +
        payment.service_lines.reduce(
          (lineTotal, line) =>
            lineTotal + Number(line.line_item_provider_payment_amount || 0),
          0
        ),
      0
    );
    const claimAdjustment = state.payments.reduce(
      (total, payment) =>
        total +
        payment.adjustments.reduce(
          (adjustmentTotal, adjustment) =>
            adjustmentTotal + Number(adjustment.adjustment_amount || 0),
          0
        ),
      0
    );
    return {
      paymentApplied,
      claimAdjustment,
      netReimbursement: paymentApplied - claimAdjustment,
      unapplied: Number(state.total || 0) - paymentApplied,
    };
  }, [state.payments, state.total]);

  const updatePayment = (index: number, next: PaymentState) =>
    setState(current => ({
      ...current,
      payments: current.payments.map((payment, paymentIndex) =>
        paymentIndex === index ? next : payment
      ),
    }));

  const validateMoney = (
    errorsByField: Record<string, string>,
    field: string,
    label: string,
    value: string,
    allowNegative = false
  ) => {
    if (!value.trim()) return;
    const allowedPattern = allowNegative
      ? /^-?\d+(\.\d{1,2})?$/
      : MONEY_PATTERN;
    if (!allowedPattern.test(value.trim())) {
      errorsByField[field] = `${label} must have no more than 2 decimal places`;
    }
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (mode === 'create') {
      if (!state.payor_id) nextErrors.payor_id = 'Insurance is required';
      if (!state.check_date) nextErrors.check_date = 'Check date is required';
      if (!state.deposit_date)
        nextErrors.deposit_date = 'Deposit date is required';
      if (!state.trace_number)
        nextErrors.trace_number = 'Remittance # is required';
      if (!state.total) nextErrors.total = 'Check total is required';
    }
    validateMoney(nextErrors, 'total', 'Check total', state.total);
    if (state.total && !nextErrors.total) {
      const num = parseFloat(state.total);
      if (isNaN(num) || num < 0) {
        nextErrors.total = 'Check total must be a valid dollar amount';
      }
    }
    for (const dateField of ['check_date', 'deposit_date'] as const) {
      const value = state[dateField];
      if (value && value > getTodayInPT()) {
        nextErrors[dateField] =
          `${dateField === 'check_date' ? 'Check' : 'Deposit'} date cannot be in the future`;
      }
    }
    state.payments.forEach((payment, paymentIndex) => {
      if (
        payment.claim_status_code &&
        !isKnownClaimStatus(payment.claim_status_code) &&
        !fileBacked
      ) {
        nextErrors[`payment-${paymentIndex}-status`] =
          'Claim status must be selected from the supported CLP02 codes';
      }
      validateMoney(
        nextErrors,
        `payment-${paymentIndex}-charge`,
        'Claim charge',
        payment.total_claim_charge_amount
      );
      validateMoney(
        nextErrors,
        `payment-${paymentIndex}-paid`,
        'Payment amount',
        payment.claim_payment_amount,
        true
      );
      validateMoney(
        nextErrors,
        `payment-${paymentIndex}-responsibility`,
        'Patient responsibility',
        payment.patient_responsibility_amount
      );
      payment.adjustments.forEach((adjustment, adjustmentIndex) => {
        const field = `payment-${paymentIndex}-adjustment-${adjustmentIndex}`;
        if (
          !adjustment.reason_code.trim() &&
          !adjustment.preserveBlankReasonCode
        ) {
          nextErrors[`${field}-reason`] = 'Reason code is required';
        }
        if (!adjustment.adjustment_amount.trim()) {
          nextErrors[field] = 'Adjustment amount is required';
        } else {
          validateMoney(
            nextErrors,
            field,
            'Adjustment amount',
            adjustment.adjustment_amount,
            true
          );
        }
      });
      payment.service_lines.forEach((line, lineIndex) => {
        validateMoney(
          nextErrors,
          `payment-${paymentIndex}-line-${lineIndex}-charge`,
          'Line charge',
          line.line_item_charge_amount
        );
        validateMoney(
          nextErrors,
          `payment-${paymentIndex}-line-${lineIndex}-paid`,
          'Line payment',
          line.line_item_provider_payment_amount,
          true
        );
        line.adjustments.forEach((adjustment, adjustmentIndex) => {
          const field = `payment-${paymentIndex}-line-${lineIndex}-adjustment-${adjustmentIndex}`;
          if (
            !adjustment.reason_code.trim() &&
            !adjustment.preserveBlankReasonCode
          ) {
            nextErrors[`${field}-reason`] = 'Reason code is required';
          }
          if (!adjustment.adjustment_amount.trim()) {
            nextErrors[field] = 'Adjustment amount is required';
          } else {
            validateMoney(
              nextErrors,
              field,
              'Adjustment amount',
              adjustment.adjustment_amount,
              true
            );
          }
        });
      });
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const toAdjustment = (
    adjustment: AdjustmentState
  ): RemittanceAdjustmentUpsert => ({
    ...(adjustment.id ? { id: adjustment.id } : {}),
    group_code: adjustment.group_code,
    reason_code: adjustment.reason_code,
    adjustment_amount: adjustment.adjustment_amount,
  });

  const toLine = (line: ServiceLineState): RemittanceServiceLineUpsert => ({
    ...(line.id ? { id: line.id } : {}),
    service_date: line.serviceDateFromRange ? null : line.service_date || null,
    line_item_charge_amount: line.line_item_charge_amount || null,
    line_item_provider_payment_amount:
      line.line_item_provider_payment_amount || null,
    units_of_service_paid_count: line.units_of_service_paid_count || null,
    remark_codes: splitCodes(line.remark_codes),
    adjustments: line.adjustments.map(toAdjustment),
  });

  const toPayment = (payment: PaymentState): RemittancePaymentUpsert => ({
    ...(payment.id ? { id: payment.id } : {}),
    claim_id: payment.claim_id || null,
    claim_status_code: payment.claim_status_code || null,
    total_claim_charge_amount: payment.total_claim_charge_amount || null,
    claim_payment_amount: payment.claim_payment_amount || null,
    patient_responsibility_amount:
      payment.patient_responsibility_amount || null,
    facility_type_code: payment.facility_type_code || null,
    claim_frequency_code: payment.claim_frequency_code || null,
    remark_codes: splitCodes(payment.remark_codes),
    service_lines: payment.service_lines.map(toLine),
    adjustments: payment.adjustments.map(toAdjustment),
  });

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const body: RemittanceManualCreate | RemittanceWorksheetUpdate = {
      payor_id: state.payor_id || null,
      deposit_date: state.deposit_date || null,
      comments: state.comments || null,
      check_issue_or_eft_effective_date: state.check_date || null,
      check_or_eft_trace_number: state.trace_number || null,
      total_actual_provider_payment_amount: state.total || null,
      payment_method_code: state.payment_method_code || null,
      payments: state.payments.map(toPayment),
    };
    try {
      const response =
        mode === 'create'
          ? await createRemittance({ body })
          : await updateRemittanceWorksheet({
              path: { remittance_id: remittanceId ?? '' },
              body,
            });
      if (response.error || !response.data) {
        showError(
          getBusinessErrorMessage(response) ?? 'Failed to save remittance'
        );
        return;
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save remittance worksheet', error);
      showError('Failed to save remittance');
    } finally {
      setSaving(false);
    }
  };

  const attachClaim = (claim: RemittanceClaimSearchRow) => {
    setState(current => {
      if (attachPaymentIndex !== null) {
        return {
          ...current,
          payments: current.payments.map((payment, index) =>
            index === attachPaymentIndex
              ? {
                  ...payment,
                  claim_id: claim.id,
                  claim_number: claim.claim_number ?? '',
                  linkedClaimPatientName: claim.patient_name,
                  linkedClaimPatientControlNumber:
                    claim.patient_control_number ?? '',
                }
              : payment
          ),
        };
      }
      return {
        ...current,
        payments: [
          ...current.payments,
          {
            claim_id: claim.id,
            claim_number: claim.claim_number ?? '',
            linkedClaimPatientName: claim.patient_name,
            linkedClaimPatientControlNumber: claim.patient_control_number ?? '',
            patient_control_number: '',
            patientControlFileDerived: false,
            payer_claim_control_number: '',
            claim_status_code: '',
            total_claim_charge_amount: claim.total_charges ?? '',
            claim_payment_amount: '',
            patient_responsibility_amount: '',
            facility_type_code: '',
            claim_frequency_code: '',
            remark_codes: '',
            service_lines: [],
            adjustments: [],
          },
        ],
      };
    });
    setAttachPaymentIndex(null);
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-h-[90vh] max-w-6xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'New remittance worksheet'
              : 'Remittance worksheet'}
          </DialogTitle>
          <DialogDescription>
            {completed
              ? 'Completed remittances are read-only. Reopen the worksheet to make changes.'
              : 'Review the remittance header, claim payments, and adjustments before saving.'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            Loading worksheet…
          </p>
        ) : loadError ? (
          <div className='flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
            <AlertCircle className='h-4 w-4' /> Unable to load the worksheet.
            Please try again.
          </div>
        ) : (
          <div className='space-y-5'>
            <section className='rounded-lg border border-slate-200 bg-slate-50/60 p-4'>
              <div className='mb-3 flex items-center justify-between'>
                <h3 className='text-xs font-semibold uppercase tracking-[0.14em] text-slate-600'>
                  Remittance header
                </h3>
                {fileBacked && <Badge variant='outline'>835-backed</Badge>}
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4'>
                <div className='space-y-1'>
                  <DropdownField
                    label='Insurance'
                    htmlFor='remittance-payor'
                    value={state.payor_id}
                    onChange={payor_id =>
                      setState(current => ({ ...current, payor_id }))
                    }
                    options={payors}
                    disabled={completed || payorsLoading || payorsError}
                    isLoading={payorsLoading}
                    error={errors.payor_id}
                    required={mode === 'create'}
                    fieldSize='sm'
                    variant='formSelect'
                  />
                  {payorsError && (
                    <div
                      role='alert'
                      className='flex flex-wrap items-center gap-2 text-xs text-red-700'
                    >
                      <span>Unable to load insurance options.</span>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        aria-label='Retry payors'
                        onClick={() => void loadPayors()}
                      >
                        <RefreshCw className='mr-1 h-3 w-3' /> Retry
                      </Button>
                    </div>
                  )}
                </div>
                <div role='group' aria-label='Check date field'>
                  <DateField
                    label='Check date'
                    htmlFor='remittance-check-date'
                    value={state.check_date || null}
                    onChange={check_date =>
                      setState(current => ({
                        ...current,
                        check_date: check_date ?? '',
                      }))
                    }
                    disabled={identityLocked}
                    error={errors.check_date}
                    required={mode === 'create'}
                    fieldSize='sm'
                    extra={<LockHint visible={fileBacked} />}
                  />
                </div>
                <div role='group' aria-label='Deposit date field'>
                  <DateField
                    label='Deposit date'
                    htmlFor='remittance-deposit-date'
                    value={state.deposit_date || null}
                    onChange={deposit_date =>
                      setState(current => ({
                        ...current,
                        deposit_date: deposit_date ?? '',
                      }))
                    }
                    disabled={completed}
                    error={errors.deposit_date}
                    required={mode === 'create'}
                    fieldSize='sm'
                  />
                </div>
                <Field
                  label='Remittance #'
                  htmlFor='remittance-trace-number'
                  value={state.trace_number}
                  onChange={trace_number =>
                    setState(current => ({ ...current, trace_number }))
                  }
                  disabled={identityLocked}
                  error={errors.trace_number}
                  required={mode === 'create'}
                  fieldSize='sm'
                  extra={<LockHint visible={fileBacked} />}
                />
                <Field
                  label='Check total'
                  htmlFor='remittance-total'
                  value={state.total}
                  onChange={total =>
                    setState(current => ({ ...current, total }))
                  }
                  disabled={identityLocked}
                  error={errors.total}
                  required={mode === 'create'}
                  fieldSize='sm'
                  inputProps={{ inputMode: 'decimal' }}
                  extra={<LockHint visible={fileBacked} />}
                />
                <DropdownField
                  label='Payment method'
                  htmlFor='remittance-payment-method'
                  value={state.payment_method_code}
                  onChange={payment_method_code =>
                    setState(current => ({ ...current, payment_method_code }))
                  }
                  options={PAYMENT_METHOD_OPTIONS}
                  disabled={identityLocked}
                  fieldSize='sm'
                  variant='formSelect'
                  extra={<LockHint visible={fileBacked} />}
                />
                <div className='md:col-span-2'>
                  <Field
                    label='Comments'
                    htmlFor='remittance-comments'
                    value={state.comments}
                    onChange={comments =>
                      setState(current => ({ ...current, comments }))
                    }
                    disabled={completed}
                    multiline
                    fieldSize='sm'
                  />
                </div>
              </div>
            </section>

            <section className='grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-slate-200 sm:grid-cols-4'>
              {[
                ['Payment applied to claims', rollups.paymentApplied],
                ['Claim adjustment', rollups.claimAdjustment],
                ['Claim net reimbursement', rollups.netReimbursement],
                ['Unapplied remittance payment', rollups.unapplied],
              ].map(([label, value]) => (
                <div
                  key={label as string}
                  role='group'
                  aria-label={label as string}
                  className='bg-white p-3'
                >
                  <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    {label as string}
                  </div>
                  <div className='mt-1 text-lg font-semibold tabular-nums'>
                    {currency(String(value))}
                  </div>
                </div>
              ))}
            </section>

            <section className='space-y-3'>
              <div className='flex flex-wrap items-center justify-between gap-2'>
                <div>
                  <h3 className='text-base font-semibold'>Claim payments</h3>
                  <p className='text-sm text-muted-foreground'>
                    Edit the values that will post when this worksheet is
                    completed.
                  </p>
                </div>
                {!completed && (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setAttachPaymentIndex(null);
                      setAddClaimsOpen(true);
                    }}
                  >
                    <Plus className='mr-1 h-3 w-3' /> Add claims
                  </Button>
                )}
              </div>
              {state.payments.length === 0 ? (
                <div className='rounded border border-dashed p-5 text-sm text-muted-foreground'>
                  No claims attached. Use Add claims to create a payment row.
                </div>
              ) : (
                state.payments.map((payment, index) => (
                  <ClaimPaymentEditor
                    key={payment.id ?? `${payment.claim_id}-${index}`}
                    payment={payment}
                    source={source}
                    disabled={completed}
                    errors={errors}
                    paymentIndex={index}
                    onChange={next => updatePayment(index, next)}
                    onRemove={() =>
                      setState(current => ({
                        ...current,
                        payments: current.payments.filter(
                          (_, paymentIndex) => paymentIndex !== index
                        ),
                      }))
                    }
                    onAttachClaim={() => {
                      setAttachPaymentIndex(index);
                      setAddClaimsOpen(true);
                    }}
                    onDetachClaim={() =>
                      updatePayment(index, {
                        ...payment,
                        claim_id: '',
                        claim_number: '',
                        linkedClaimPatientName: '',
                        linkedClaimPatientControlNumber: '',
                      })
                    }
                  />
                ))
              )}
            </section>
            {(detail?.provider_adjustments ?? []).length > 0 && (
              <section className='space-y-2 rounded-lg border border-slate-200 p-4'>
                <div>
                  <h3 className='text-sm font-semibold'>
                    Provider-level adjustments
                  </h3>
                  <p className='text-xs text-muted-foreground'>
                    PLB values from the 835 are displayed for reference and are
                    not editable in this worksheet.
                  </p>
                </div>
                <div className='overflow-x-auto'>
                  <table className='w-full text-left text-sm'>
                    <thead className='text-xs uppercase tracking-wide text-muted-foreground'>
                      <tr>
                        <th className='py-2 pr-3'>Group</th>
                        <th className='py-2 pr-3'>Reason</th>
                        <th className='py-2 text-right'>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail?.provider_adjustments ?? []).map(adjustment => (
                        <tr key={adjustment.id} className='border-t'>
                          <td className='py-2 pr-3'>
                            {adjustment.group_code ?? '—'}
                          </td>
                          <td className='py-2 pr-3'>
                            {adjustment.reason_code}
                          </td>
                          <td className='py-2 text-right tabular-nums'>
                            {currency(adjustment.adjustment_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {Object.keys(errors).length > 0 && (
              <div
                role='alert'
                className='flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700'
              >
                <AlertCircle className='h-4 w-4 shrink-0' />
                Fix the highlighted worksheet fields before saving.
              </div>
            )}
            <Separator />
            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              {!completed && (
                <Button
                  type='button'
                  size='sm'
                  onClick={() => void save()}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save remittance'}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <AddClaimsDialog
        open={addClaimsOpen}
        onOpenChange={open => {
          setAddClaimsOpen(open);
          if (!open) setAttachPaymentIndex(null);
        }}
        onAttach={attachClaim}
        unavailableClaimIds={unavailableClaimIds}
      />
    </Dialog>
  );
}
